import { useContext, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat, faDownload } from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, Image, FileInput, Form, ModalContext, SidebarModal,
  Grid,
} from '@domain-group/suit-theme'

import useBrand, { useBrandForm } from '@hooks/useBrand'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const defaultState = {
  showImage: false,
  type: '',
}

interface UploadBrandImageModalProps {
  callbacks: any,
  modalKey: string,
  showModal: boolean,
}

const UploadBrandImageModal = (
  { modalKey = 'UploadBrandImageModal', callbacks, showModal }: UploadBrandImageModalProps,
) => {
  const { closeModal, updateBrand } = callbacks || {}

  const [state, setState] = useSetState(defaultState)
  const { showImage, type } = state

  const modalContext: any = useContext(ModalContext as any)
  const { modalData } = modalContext

  const modalPayload = modalData[modalKey] || {}
  const { brand, type: initType } = modalPayload

  const logo: string = brand?.logo?.small
  const monoLogo: string = brand?.mono_logo?.small

  useEffect(() => setState({ type: initType }), [])

  useEffect(() => {
    if ((type === 'logo' && !!logo) || (type === 'mono_logo' && !!monoLogo)){
      setState({ showImage: true })
    } else {
      setState({ showImage: false })
    }
  }, [brand.id, type])

  const {
    entityState,
    setEntityState,
  } = useBrandForm(brand, { validateOn: 'change' })

  const {
    callbacks: { updateBrand: updateFn },
    updating,
  } = useBrand(brand)

  const uploadBrandImagePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.brand,
    toastText: 'Brand Image Updated',
  }

  const imageUrl = type === 'logo' ? logo : monoLogo

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Upload" titleSecondLine="Image" />

      <SidebarModal.Content>
        <Form>
          {/*
           // @ts-expect-error TS2322 */}
          <Grid gridRowGap="large">
            <Form.Field label="* Type" marginTop="large">
              <select onChange={e => setState({ type: e.target.value })} value={type}>
                <option value="">Please Select...</option>
                <option value="logo">Logo</option>
                <option value="mono_logo">Mono Logo</option>
              </select>
            </Form.Field>

            {!showImage && (
              <Form.Field label="* Upload Logos">
                <FileInput
                  input={{
                    // @ts-expect-error TS7006
                    onChange: (fileData, fileObject) => (
                      setEntityState({ [`${type}_file`]: fileData, [`${type}_file_name`]: fileObject.name })
                    ),
                  }}
                />
              </Form.Field>
            )}

            {showImage && (
              <>
                <Form.Field label="Image">
                  <Box justifyContent="center">
                    <Image
                      backgroundSize="contain"
                      borderRadius={5}
                      boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
                      height={200}
                      style={{ backgroundColor: type === 'logo' ? `#${brand.colour_primary}` : null }}
                      url={imageUrl}
                    />
                  </Box>
                </Form.Field>

                {/*
                 // @ts-expect-error TS2322 */}
                <Grid gridRowGap="medium">
                  <Form.Field label="More Options">
                    <Button
                      buttonStyle="secondaryUtility"
                      icon={<FontAwesomeIcon icon={faRepeat} />}
                      onClick={() => setState({ showImage: false })}
                      size="medium"
                    >
                      Replace Image
                    </Button>
                  </Form.Field>

                  <a href={imageUrl} target="_blank" rel="noreferrer">
                    <Button
                      buttonStyle="secondaryUtility"
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      size="medium"
                    >
                      Download Image
                    </Button>
                  </a>
                </Grid>
              </>
            )}
          </Grid>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!type}
          loading={updating}
          onClick={() => updateBrand(uploadBrandImagePayload)}
          size="large"
        >
          Save Logo
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: UploadBrandImageModalProps) => (
  <SidebarModal.RenderController {...props}>
    <UploadBrandImageModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
